<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <div class="relative">
              <VaAvatar :size="32" color="info" icon="mso-person" />
              <!-- Status indicator -->
              <div
                class="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white"
                :class="{
                  'bg-green-500': currentStatus === 'online',
                  'bg-yellow-500': currentStatus === 'away',
                  'bg-red-500': currentStatus === 'offline',
                }"
              />
            </div>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in options" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            :class="{
              'bg-gray-100': item.isStatus && item.statusValue === currentStatus,
              'opacity-50 pointer-events-none': item.isStatus && isChangingStatus,
            }"
            v-bind="resolveLinkAttribute(item)"
          >
            <VaIcon
              :name="item.icon"
              class="pr-1"
              :color="item.isStatus ? getStatusIconColor(item.statusValue || '') : 'secondary'"
            />
            {{ t(`user.${item.name}`) }}
            <VaIcon
              v-if="item.isStatus && item.statusValue === currentStatus && !isChangingStatus"
              name="mso-check"
              class="ml-auto"
              color="success"
            />
            <VaProgressCircle
              v-if="item.isStatus && isChangingStatus"
              class="ml-auto"
              :size="16"
              :thickness="2"
              indeterminate
            />
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'
import { logout, fetchUserStatus, changeUserStatus } from '../../../../services/auth-service'
import { useRouter } from 'vue-router'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()
const router = useRouter()

// Status management
const currentStatus = ref<'online' | 'offline' | 'away'>('offline')
const isChangingStatus = ref(false)

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
  isStatus?: boolean
  statusValue?: 'online' | 'offline' | 'away'
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'status',
        separator: true,
        list: [
          {
            name: 'online',
            icon: 'mso-circle',
            isStatus: true,
            statusValue: 'online',
          },
          {
            name: 'away',
            icon: 'mso-schedule',
            isStatus: true,
            statusValue: 'away',
          },
          {
            name: 'offline',
            icon: 'mso-circle',
            isStatus: true,
            statusValue: 'offline',
          },
        ],
      },
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'preferences',
            icon: 'mso-account_circle',
          },
          {
            name: 'settings',
            to: 'settings',
            icon: 'mso-settings',
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'logout',
            icon: 'mso-logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

// Status color mapping
const statusColorMap = {
  online: 'success',
  away: 'warning',
  offline: 'danger',
} as const

// Get status icon color based on status
const getStatusIconColor = (status: string) => {
  return statusColorMap[status as keyof typeof statusColorMap] || 'secondary'
}

const handleItemClick = async (item: ProfileListItem) => {
  if (item.name === 'logout') {
    logout(router)
    isShown.value = false
  } else if (item.isStatus && item.statusValue) {
    await handleStatusChange(item.statusValue)
  }
}

const handleStatusChange = async (newStatus: 'online' | 'offline' | 'away') => {
  if (isChangingStatus.value || currentStatus.value === newStatus) {
    return
  }

  isChangingStatus.value = true
  try {
    const success = await changeUserStatus(newStatus)
    if (success) {
      currentStatus.value = newStatus
    }
  } catch (error) {
    console.error('Failed to change status:', error)
  } finally {
    isChangingStatus.value = false
    isShown.value = false
  }
}

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.name === 'logout' || item.isStatus) {
    return { onClick: () => handleItemClick(item) }
  }
  return item.to ? { to: { name: item.to } } : item.href ? { href: item.href, target: '_blank' } : {}
}

// Fetch user status on component mount
onMounted(async () => {
  try {
    const status = await fetchUserStatus()
    if (status && ['online', 'offline', 'away'].includes(status)) {
      currentStatus.value = status as 'online' | 'offline' | 'away'
    }
  } catch (error) {
    console.error('Failed to fetch user status:', error)
  }
})
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
